import { Avatar, Stack, Typography, useMediaQuery } from '@mui/material';
import PWDKDialog from 'components/PWDKDialog';
import PWDKButton from 'components/new/PWDKButton';
import PWDKLoadingButton from 'components/new/PWDKLoadingButton';
import useSearchQuery from 'hooks/useSearchQuery';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import axiosInstance from 'utils/axios';
import { appConfig } from 'utils/config';

const ApproveRemoveModal = ({
  open,
  onClose,
  type,
  tribeMemberData,
  refetch,
}) => {
  const { profilePicture, nameMember, user, id } = tribeMemberData || {};
  const { tribeId } = useSearchQuery();
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const { title, warning, cta } = useMemo(() => {
    if (type === 'approve') {
      return {
        title: 'Approve Member',
        warning:
          'Confirm your decision to approve this member by reviewing the provided information.',
        cta: 'Approve',
      };
    }
    return {
      title: 'Remove Member',
      warning:
        'Are you sure you want to remove this member? You cannot undo this process.',
      cta: 'Yes, Remove',
    };
  }, [type]);

  const handleApproveTribeMember = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.put(
        '/v2/tribe-member/approve-member/tribe-leader',
        null,
        {
          params: {
            tribeMemberId: id,
            tribeId: Number(tribeId),
          },
        },
      );
      if (response) {
        refetch();
        setLoading(false);
        enqueueSnackbar('User has been successfully added', {
          variant: 'default',
        });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleDeleteTribeMember = async () => {
    try {
      const response = await axiosInstance.delete(
        '/v2/tribe-member/decline-member/tribe-leader',
        {
          params: {
            tribeMemberId: id,
            tribeId: Number(tribeId),
            disableNotification: 'true',
          },
        },
      );
      if (response) {
        refetch();
        setLoading(false);
        enqueueSnackbar('User has been successfully deleted', {
          variant: 'default',
        });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const onCickCta = () => {
    if (type === 'approve') {
      handleApproveTribeMember();
    }

    if (type === 'remove') {
      handleDeleteTribeMember();
    }
  };

  return (
    <PWDKDialog
      open={open}
      title={title}
      dialogActions={
        <Stack direction="row" spacing={1}>
          <PWDKButton
            size={isMobile ? 'medium' : 'large'}
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </PWDKButton>
          <PWDKLoadingButton
            size={isMobile ? 'medium' : 'large'}
            variant="primary"
            onClick={onCickCta}
            loading={loading}
          >
            {cta}
          </PWDKLoadingButton>
        </Stack>
      }
      onBackdropClick={onClose}
    >
      <Stack spacing={2}>
        <Typography typography={{ md: 'paragraph' }} color="neutral300.main">
          {warning}
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          p={{ md: '0px 16px 16px', xs: '8px 16px' }}
        >
          <Avatar
            src={`${appConfig.baseUrl}${profilePicture}`}
            alt={nameMember}
            sx={{
              width: { md: '40px' },
              height: { md: '40px' },
              borderRadius: '100%',
            }}
          />
          <Stack spacing={0.5}>
            <Typography
              typography={{ md: 'subheading3medium', xs: 'caption1' }}
              color="neutral100.main"
            >
              {nameMember}
            </Typography>
            <Typography
              typography={{
                md: 'captionreguler',
                xs: 'metatagreguler',
              }}
              color="neutral400.main"
            >
              {user?.phone}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </PWDKDialog>
  );
};

export default ApproveRemoveModal;
